/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {MatDialog} from "@angular/material/dialog";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanBaseState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-base.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {ClinicalDataType, TaskDiaryResponse, TrecTask} from "@trec/api/clinical-data";
import {RangeDatesUnix} from "@trec/api/common";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {StoreAction} from "@trec/components/store";
import {subWeeks} from "date-fns";
import {Observable, of, Subscription} from "rxjs";
import {TpBaseActions} from "../../../store/patient-treatment-plan-base.actions";
import {
  EditTaskDialogComponent,
  EditTaskDialogComponentData
} from "../../edit/edit-task-dialog/edit-task-dialog.component";
import {TaskDeleteDialogComponent} from "../../others/task-delete-dialog/task-delete-dialog.component";

@Component({
  selector: "c3-config-tasks",
  templateUrl: "./config-tasks.component.html",
  styleUrls: ["./config-tasks.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigTasksComponent
  extends ConfigSectionComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Select(PatientTreatmentPlanBaseState.tasks) tasks$: Observable<TrecTask[]>;
  @Select(PatientTreatmentPlanBaseState.tasksDiary) tasksDiary$: Observable<TaskDiaryResponse[]>;
  @Select(hasActionsExecuting([TpBaseActions.FetchTasks, TpBaseActions.DeleteTask]))
  loading$: Observable<boolean>;

  // Table vars ///////////////////////////////////////////////////////////////////////////////////

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input()
  context: RegistryContexts[];

  @Input()
  AI: boolean;


  get touched$(): Observable<boolean> {
    return of(true);
  }

  get valid$(): Observable<boolean> {
    return of(true);
  }

  /**
   * Show ended examinations.
   */
  showEnded = false;

  displayedColumns: string[] = ["data_type", "start_date", "end_date", "frequency", "doctor", "actions"];
  dataSource: MatTableDataSource<TrecTask> = new MatTableDataSource();

  // Chart vars ///////////////////////////////////////////////////////////////////////////////////

  readonly viewPeriod: RangeDatesUnix = {
    start: subWeeks(new Date(), 2).valueOf(),
    end: subWeeks(new Date(), 1).valueOf(),
  };

  // Others ///////////////////////////////////////////////////////////////////////////////////////

  viewType: "table" | "chart" = "table";

  readonly today = new Date().valueOf();

  touched: boolean;
  valid: boolean;

  private subscr: Subscription;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private c3Statusbar: C3SnackbarStatusService
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(new TpBaseActions.FetchTasks({diaryPeriod: this.viewPeriod}));

    // Override filter function
    this.dataSource.filterPredicate = (data: TrecTask, filter: string) => {
      if (filter === "show-all") {
        // Also ended items
        return true;
      } else if (filter === "hide-ended") {
        return data.metadata.end_date > this.today;
      } else {
        return true;
      }
    };

    // Define how sort function can access data using keys
    this.dataSource.sortingDataAccessor = (data: TrecTask, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case "data_type":
          return data.data.data_type;
        case "start_date":
          return data.metadata.start_date;
        case "end_date":
          return data.metadata.end_date;
        case "frequency":
          return `${data.data.regular.frequency.values[0]} ${data.data.regular.frequency.type}`;

        default:
          return "";
      }
    };

    // Setup default filter
    this.dataSource.filter = "hide-ended";
  }

  ngAfterViewInit() {
    // Init table
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.subscr = this.tasks$.subscribe((tasks) => {
      this.dataSource.data = tasks;
      this.cd.markForCheck();
    });
  }

  // View and Edit ////////////////////////////////////////////////////////////////////////////////

  canEditOrDelete(doctorId?: string) {
    if (!doctorId) {
      return false;
    }
    const id = this.store.selectSnapshot(CommonState.userId);

    return id === doctorId;
  }

  openEditDialog(task: TrecTask | null = null) {
    const canEditOrDelete = this.canEditOrDelete(task?.metadata.doctor?.id);
    this.dialog.open(EditTaskDialogComponent, {
      width: "90%",
      minWidth: "720px",
      data: {task, AI: this.AI, context: this.context, canEditOrDelete} as EditTaskDialogComponentData,
    });
  }

  onDeleteTask(task: TrecTask) {
    const dialogRef = this.dialog.open(TaskDeleteDialogComponent, {
      data: {itemType: ClinicalDataType.TASK},
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.store.dispatch(new TpBaseActions.DeleteTask({task})).subscribe(() => {
          this.c3Statusbar.showSuccess({message: `Task eliminato`});
        });
      }
    });
  }

  toggleShowEnded(event: MatCheckboxChange) {
    this.showEnded = event.checked;

    if (this.showEnded) {
      // Show all
      this.dataSource.filter = "show-all";
    } else {
      this.dataSource.filter = "hide-ended";
    }
    this.cd.markForCheck();
  }

  onRefreshList() {
    this.store.dispatch(new TpBaseActions.FetchTasks({diaryPeriod: this.viewPeriod}));
  }

  prepareSaveDataAction(): StoreAction | null {
    return null;
  }

  resetTouched(): void { }

  ngOnDestroy(): void {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }
  }
}
